import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OLAF } from 'HubStyleTokens/colors';
import { CARD_PADDING, CARD_PADDING_COMPACT, GRID_BREAKPOINT_EXTRA_LARGE, GRID_BREAKPOINT_LARGE } from 'HubStyleTokens/sizes';
import H4 from 'UIComponents/elements/headings/H4';
import UIButton from 'UIComponents/button/UIButton';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UIMultiColumn from 'UIComponents/layout/UIMultiColumn';
import UIMultiColumnGroup from 'UIComponents/layout/UIMultiColumnGroup';
import UIMultiColumnItem from 'UIComponents/layout/UIMultiColumnItem';
import UILink from 'UIComponents/link/UILink'; // @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message

import calloutBlobsImageUrl from 'bender-url!ecosystem-marketplace-core/img/callout-blobs.svg'; // @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message

import calloutWaveImageUrl from 'bender-url!ecosystem-marketplace-core/img/callout-wave.svg';
import { FEATURED_CALLOUT_COLOR_TYPES, FEATURED_CALLOUT_COLORS, FEATURED_CALLOUT_LAYOUT_TYPES } from '../../constants/FeaturedCallouts';
import * as ScreenSizes from '../../constants/layout/ScreenSizes'; // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../l... Remove this comment to see the full error message

import MediaQuery from '../layout/MediaQuery';
const WAVE_HEIGHT = 100;
const BLOB_LAYOUT_BREAKPOINTS = {
  [FEATURED_CALLOUT_LAYOUT_TYPES.FULL_WIDTH]: GRID_BREAKPOINT_LARGE,
  [FEATURED_CALLOUT_LAYOUT_TYPES.MULTI]: GRID_BREAKPOINT_EXTRA_LARGE
};

const calloutHeroGradient = ({
  primaryBackgroundColor,
  secondaryBackgroundColor
}) => `linear-gradient(-303deg, ${primaryBackgroundColor}, ${secondaryBackgroundColor})`;

const CalloutHero = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["layout", "primaryBackgroundColor", "secondaryBackgroundColor"]);

  return /*#__PURE__*/_jsx(UICardSection, Object.assign({}, rest, {
    scrollable: true
  }));
}).withConfig({
  displayName: "FeaturedCallout__CalloutHero",
  componentId: "sc-1wdr4qg-0"
})(["position:relative;padding:", " ", " ", " ", ";overflow:hidden;background-color:", ";background-image:", " ", ";background-position:top right;background-repeat:no-repeat;background-size:auto 110%;color:", ";&::after{content:'';display:block;height:", "px;width:110%;position:absolute;left:-5%;right:-5%;bottom:-", "px;background-image:url(", ");background-repeat:no-repeat;background-size:100% 100%;}&&.is--responsive{@media (max-width:", "){padding:", " ", " ", " ", ";}@media (max-width:", "){background-image:", ";}}"], CARD_PADDING_COMPACT, CARD_PADDING, CARD_PADDING, CARD_PADDING_COMPACT, props => props.primaryBackgroundColor, props => props.layout === FEATURED_CALLOUT_LAYOUT_TYPES.FULL_WIDTH ? `url(${calloutBlobsImageUrl}),` : '', calloutHeroGradient, OLAF, WAVE_HEIGHT, WAVE_HEIGHT / 2, calloutWaveImageUrl, GRID_BREAKPOINT_EXTRA_LARGE, CARD_PADDING_COMPACT, CARD_PADDING_COMPACT, CARD_PADDING, CARD_PADDING_COMPACT, ({
  layout
}) => BLOB_LAYOUT_BREAKPOINTS[layout], calloutHeroGradient);
CalloutHero.displayName = 'CalloutHero';

const FeaturedCallout = ({
  buttonText,
  gradientColor,
  description,
  external,
  headline,
  illustrationName,
  onClick,
  layout = FEATURED_CALLOUT_LAYOUT_TYPES.FULL_WIDTH,
  Link = UILink,
  linkProps = {},
  responsive = true
}) => {
  const [primaryBackgroundColor, secondaryBackgroundColor] = useMemo(() => FEATURED_CALLOUT_COLORS[gradientColor], [gradientColor]);
  const computedHeroClasses = useMemo(() => responsive ? 'is--responsive' : "", [responsive]);
  const computedLinkProps = useMemo(() => {
    let _linkProps = Object.assign({}, linkProps, {
      onClick,
      style: {
        display: 'flex',
        alignItems: 'stretch'
      },
      use: 'unstyled'
    });

    if (external) {
      _linkProps = Object.assign({}, _linkProps, {
        external: true,
        iconProps: {
          className: 'hidden'
        }
      });
    }

    return _linkProps;
  }, [external, onClick, linkProps]);
  const renderedIllustrationColumnItem = useMemo(() => {
    if (layout === FEATURED_CALLOUT_LAYOUT_TYPES.MULTI) {
      return null;
    }

    const illustrationColumnItem = /*#__PURE__*/_jsx(UIMultiColumnItem, {
      horizontalAlign: "center",
      verticalAlign: "top",
      width: 272,
      children: /*#__PURE__*/_jsx(UIIllustration, {
        height: "100%",
        name: illustrationName,
        style: {
          minHeight: 72,
          maxHeight: 100
        }
      })
    });

    return responsive ? /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: ScreenSizes.LG,
      children: illustrationColumnItem
    }) : illustrationColumnItem;
  }, [illustrationName, layout, responsive]);
  return /*#__PURE__*/_jsx(Link, Object.assign({}, computedLinkProps, {
    children: /*#__PURE__*/_jsxs(UICardWrapper, {
      compact: true,
      children: [/*#__PURE__*/_jsx(CalloutHero, {
        className: computedHeroClasses,
        layout: layout,
        primaryBackgroundColor: primaryBackgroundColor,
        secondaryBackgroundColor: secondaryBackgroundColor,
        children: /*#__PURE__*/_jsx(UIMultiColumn, {
          flush: true,
          style: {
            background: 'none'
          },
          children: /*#__PURE__*/_jsxs(UIMultiColumnGroup, {
            children: [/*#__PURE__*/_jsxs(UIMultiColumnItem, {
              className: "p-right=0",
              verticalAlign: "top",
              children: [/*#__PURE__*/_jsx(H4, {
                className: "m-bottom-2",
                children: headline
              }), /*#__PURE__*/_jsx("p", {
                children: description
              })]
            }), renderedIllustrationColumnItem]
          })
        })
      }), /*#__PURE__*/_jsx(UICardSection, {
        className: "p-top-0",
        children: /*#__PURE__*/_jsx(UIButton, {
          external: external,
          iconProps: {
            color: OLAF
          },
          size: "small",
          truncate: true,
          use: "tertiary",
          children: buttonText
        })
      })]
    })
  }));
};

FeaturedCallout.propTypes = {
  buttonText: PropTypes.node.isRequired,
  gradientColor: PropTypes.oneOf(Object.values(FEATURED_CALLOUT_COLOR_TYPES)).isRequired,
  description: PropTypes.node.isRequired,
  external: PropTypes.bool,
  headline: PropTypes.node.isRequired,
  illustrationName: PropTypes.string.isRequired,
  layout: PropTypes.oneOf(Object.values(FEATURED_CALLOUT_LAYOUT_TYPES)),
  Link: PropTypes.elementType,
  linkProps: PropTypes.object,
  onClick: PropTypes.func,
  responsive: PropTypes.bool
};
export default FeaturedCallout;